import React from 'react'
import { BeWizrMeetingOccurrence } from '~/models'
import { bewizrMeetingsStore } from '~/stores'
import { memo, observer } from '~/ui/component'
import { HBox, Label, List, ListBar, SVG } from '~/ui/components'
import { layout } from '~/ui/styling'
import { BeWizrEnrollmentStatus } from '../bewizr'
import BeWizrMeetingOccurrenceDateTimeView from './BeWizrMeetingOccurrenceDateTimeView'

export interface Props {
  meetingID:            string
  selectedOccurrenceID: string | null

  requestSelect: (occurrence: BeWizrMeetingOccurrence) => any
}

const BeWizrMeetingOccurrencesList = observer('BeWizrMeetingOccurrencesList', (props: Props) => {

  const {requestSelect, selectedOccurrenceID} = props

  const document = bewizrMeetingsStore.meetings.document(props.meetingID)
  const meeting  = document.data

  const occurrences = meeting?.occurrences ?? []

  //------
  // Rendering

  function render() {
    return (
      <List
        flex={false}
        data={occurrences}
        keyExtractor={keyExtractor}
        renderItem={renderOccurrence}
        itemGap={layout.padding.inline.s}
      />
    )
  }

  const keyExtractor = React.useCallback(
    (occurrence: BeWizrMeetingOccurrence) => occurrence.id,
    [],
  )

  const renderOccurrence = React.useCallback((occurrence: BeWizrMeetingOccurrence) => (
    <BeWizrMeetingOccurrencesListItem
      selected={occurrence.id === selectedOccurrenceID}
      requestSelect={requestSelect}
      occurrence={occurrence}
    />
  ), [selectedOccurrenceID, requestSelect])

  return render()

})

export default BeWizrMeetingOccurrencesList

interface BeWizrMeetingOccurrencesListItemProps {
  occurrence:     BeWizrMeetingOccurrence
  selected:      boolean
  requestSelect: (occurrence: BeWizrMeetingOccurrence) => any
}

const BeWizrMeetingOccurrencesListItem = memo('BeWizrMeetingOccurrencesListItem', (props: BeWizrMeetingOccurrencesListItemProps) => {

  const {occurrence, selected, requestSelect} = props

  const select = React.useCallback(
    () => requestSelect(occurrence),
    [requestSelect, occurrence],
  )

  //------
  // Rendering

  function render() {
    return (
      <ListBar
        caption={renderDate()}
        detail={renderMetaData()}
        accessory={renderStatus()}
        onTap={select}
        selected={selected}
      />
    )
  }

  function renderDate() {
    return (
      <BeWizrMeetingOccurrenceDateTimeView
        occurrence={occurrence}
        timeFormat={false}
        dateFormat='d LLLL yyyy'
        small
        bold
      />
    )
  }

  function renderMetaData() {
    return (
      <HBox gap={layout.padding.inline.s} wrap>
        <HBox gap={layout.padding.inline.s}>
          <SVG name='clock' size={layout.icon.xs} dim/>
          <BeWizrMeetingOccurrenceDateTimeView
            occurrence={occurrence}
            dateFormat={false}
            tiny
            dim
          />
        </HBox>
        {occurrence.location != null && (
          <HBox gap={layout.padding.inline.s}>
            <SVG name='map-pin' size={layout.icon.xs} dim/>
            <Label tiny dim>
              {occurrence.location.title}
            </Label>
          </HBox>
        )}
      </HBox>

    )
  }

  function renderStatus() {
    return (
      <BeWizrEnrollmentStatus
        isEnrolled={occurrence.isEnrolled}
        isOnWaitingList={occurrence.isOnWaitingList}
        placesLeft={occurrence.placesLeft}
        small
      />
    )
  }

  return render()

})